/**
 * Search Workspace Deals Server Function
 *
 * Performs server-side search with pagination.
 */

import { createServerFn } from "@tanstack/react-start";
import { searchWorkspaceDealsApiWorkspacesWorkspaceIdDealsSearchPost } from "@/lib/api/generated/deals/deals";
import type { SearchWorkspaceDealsRequest } from "@/lib/api/generated/searchWorkspaceDealsRequest";
import type { DealStage } from "@/lib/api/generated/dealStage";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";

interface SearchWorkspaceDealsServerRequest {
  workspaceId: string;
  request: {
    page?: number;
    page_size?: number;
    stages?: DealStage[] | null;
    search_query?: string;
    price_range?: [number | string, number | string] | number | string | null;
  };
}

export const searchWorkspaceDealsServer = createServerFn({ method: "POST" })
  .validator((data: SearchWorkspaceDealsServerRequest) => data)
  .handler(async ({ data }) => {
    const { workspaceId, request } = data;

    try {
      // Log the incoming request for debugging
      logInfo("deals.search.request", {
        workspaceId,
        request: {
          page: request.page,
          page_size: request.page_size,
          stages: request.stages,
          search_query: request.search_query,
          price_range: request.price_range,
        },
      });

      // Normalize request to API payload shape
      const normalized: SearchWorkspaceDealsRequest = {
        page: request.page ?? 1,
        page_size: request.page_size ?? 20,
        ...(request.search_query ? { search_query: request.search_query } : {}),
        // Handle stages: include if it's a non-null, non-empty array
        ...(request.stages !== null && request.stages !== undefined && request.stages.length > 0
          ? { stages: request.stages }
          : {}),
        ...(request.price_range === undefined || request.price_range === null
          ? {}
          : Array.isArray(request.price_range)
            ? {
                price_range: [
                  request.price_range[0],
                  // Handle empty string as very large number for "no upper bound"
                  request.price_range[1] === "" ? 999999999 : request.price_range[1],
                ] as [number | string, number | string],
              }
            : {
                price_range: [request.price_range as number | string] as unknown as [
                  number | string,
                  number | string,
                ],
              }),
      };
      logInfo("deals.search.normalized", { normalized });

      const response = await searchWorkspaceDealsApiWorkspacesWorkspaceIdDealsSearchPost(
        workspaceId,
        normalized,
      );

      if (response.status !== 200) {
        const errorMessage = extractApiErrorMessage(response, "Failed to search deals");
        logError("deals.search.failed", { error: errorMessage });
        throw new Error(errorMessage);
      }

      logInfo("deals.search.completed", {
        returned: response.data.items.length,
        total: response.data.total,
        page: response.data.page,
        total_pages: response.data.total_pages,
      });

      return response.data;
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while searching deals");
      logError("deals.search.error", { error: errorMessage });
      throw new Error(errorMessage);
    }
  });
